/**
 * The application shell.
 *
 * Responsibilities, and deliberately nothing else:
 *   - decide whether we are booting, signed out, or ready
 *   - lay out the rail, the main area and the detail panel
 *   - own the global keyboard map
 *   - host the dialogs that any view can raise
 *
 * Every view below it reads from the replica and renders; none of them fetch.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useWorkspace } from '@/store/workspace'
import { useUi, toHash, fromHash } from '@/store/ui'
import { boot, forgetReplica, startSync, stopSync, reportViewing } from '@/store/sync'
import { api } from '@/store/api'
import { useSyncFolder } from '@/store/sync-folder'
import { useHotkeys } from '@/hooks/useHotkeys'
import { useDesktopNotifications, useDeepLinks, useReadTracking } from '@/hooks/useDesktop'
import { TooltipProvider, Button, Spinner, EmptyState } from '@/components/ui/primitives'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Sidebar } from '@/components/Sidebar'
import { TopBar } from '@/components/TopBar'
import { CommandPalette } from '@/components/CommandPalette'
import { SignIn } from '@/views/SignIn'
import { FilesView } from '@/views/FilesView'
import { ActivityView } from '@/views/ActivityView'
import { CommentsView } from '@/views/CommentsView'
import { LocksView } from '@/views/LocksView'
import { SyncView } from '@/views/SyncView'
import { RenameDialog, DeleteDialog, UploadDialog } from '@/components/Dialogs'
import { TransferTray } from '@/components/TransferTray'
import type { ProjectId } from '@lattice/core'

export function App() {
  const status = useWorkspace(s => s.status)
  const error = useWorkspace(s => s.error)

  useEffect(() => {
    void boot()
    return () => stopSync()
  }, [])

  return (
    <TooltipProvider>
      {/* The outermost boundary is the last line of defence: without it a
          throw anywhere unmounts the entire product to a blank page. */}
      <ErrorBoundary what="Lattice">
        {status === 'booting' && <Booting />}
        {status === 'signed-out' && <SignIn onSignedIn={() => void boot()} />}
        {status === 'error' && <Broken message={error} />}
        {status === 'ready' && <Shell />}
      </ErrorBoundary>
    </TooltipProvider>
  )
}

/**
 * Shown only when there is no local replica AND the network has not answered.
 * A returning person never sees this: their files render from IndexedDB first.
 */
function Booting() {
  return (
    <div className="flex h-full items-center justify-center bg-app">
      <div className="flex items-center gap-2 text-sm text-[var(--fg-subtle)] fade-in">
        <Spinner /> Opening your workspace…
      </div>
    </div>
  )
}

function Broken({ message }: { message?: string }) {
  return (
    <div className="flex h-full items-center justify-center bg-app">
      <EmptyState
        icon={AlertTriangle}
        title="Lattice cannot reach the server"
        hint={message ?? 'Check that the API is running, then try again.'}
        action={<Button onClick={() => void boot()}>Try again</Button>}
      />
    </div>
  )
}

function Shell() {
  const projects = useWorkspace(s => s.projects)
  const { projectId, view, openPath, selection, go, open, setPaletteOpen, setView, setFilter, clearSelection } = useUi()

  const [renaming, setRenaming] = useState<{ path: string; name: string }>()
  const [deleting, setDeleting] = useState<string[]>()
  const [uploading, setUploading] = useState(false)

  /* ---- URL <-> location, so links and reloads work ---- */

  useEffect(() => {
    const apply = (): void => {
      const loc = fromHash(window.location.hash)
      if (loc) go({ projectId: loc.projectId, view: loc.view, openPath: loc.openPath })
      else if (projects.length > 0) go({ projectId: projects[0].id as ProjectId, view: 'files' })
    }
    apply()
    window.addEventListener('hashchange', apply)
    return () => window.removeEventListener('hashchange', apply)
    // Run once the projects are known; `go` is stable.
  }, [projects.length])

  useEffect(() => {
    if (!projectId) return
    const next = toHash({ projectId, view, openPath })
    if (next !== window.location.hash) window.history.replaceState(null, '', next)
  }, [projectId, view, openPath])

  /* ---- actions the shell owns ---- */

  const download = useCallback((paths?: string[]) => {
    const targets = paths ?? [...selection]
    if (!projectId || targets.length === 0) return
    void import('@/lib/transfer').then(m => m.downloadFiles(projectId, targets))
  }, [projectId, selection])

  const signOut = useCallback(async () => {
    await api.signOut().catch(() => undefined)
    // Both, and in this order. `forgetReplica` clears the workspace; the
    // folder store lives in memory beside it and holds which directories on
    // THIS machine are linked, and where they are. Leaving it behind means
    // the next person to sign in on a shared workstation finds somebody
    // else's folder paths sitting in the Changes view.
    useSyncFolder.getState().reset()
    await forgetReplica()
  }, [])

  const startRename = useCallback((path?: string) => {
    const target = path ?? openPath ?? [...selection][0]
    if (!target) return
    setRenaming({ path: target, name: target.split('/').pop() ?? target })
  }, [openPath, selection])

  /* ---- the global keyboard map ---- */

  useHotkeys([
    { combo: 'mod+k', allowInInput: true, run: () => setPaletteOpen(true), description: 'Command palette' },
    { combo: 'mod+p', allowInInput: true, run: () => setPaletteOpen(true), description: 'Command palette' },
    { combo: 'mod+u', run: () => setUploading(true), description: 'Upload files' },
    { combo: 'mod+shift+s', run: () => download(), description: 'Download selection' },
    { combo: '1', run: () => setView('files'), description: 'Files' },
    { combo: '2', run: () => setView('activity'), description: 'Activity' },
    { combo: '3', run: () => setView('comments'), description: 'Comments' },
    { combo: '4', run: () => setView('locks'), description: 'Locks' },
    { combo: '5', run: () => setView('sync'), description: 'Changes' },
    { combo: 'F2', run: () => startRename(), description: 'Rename' },
    { combo: 'Backspace', run: () => selection.size && setDeleting([...selection]), description: 'Delete selection' },
    { combo: '/', run: () => document.getElementById('tree-filter')?.focus(), description: 'Filter this project' },
    {
      combo: 'Escape',
      allowInInput: true,
      run: () => {
        if (document.activeElement instanceof HTMLInputElement) { (document.activeElement as HTMLInputElement).blur(); return }
        if (openPath) { open(undefined); return }
        if (selection.size) { clearSelection(); return }
        setFilter('')
      },
      description: 'Back out',
    },
  ])

  useEffect(() => { startSync() }, [])

  // Let colleagues see which file we have open. Sent only when it changes.
  useEffect(() => { reportViewing(projectId, openPath) }, [projectId, openPath])

  // Native notifications, deep links and the unread badge, when the desktop
  // shell is present. In a browser tab these are no-ops.
  useDesktopNotifications()
  useDeepLinks()
  useReadTracking()

  return (
    <div className="flex h-full overflow-hidden bg-app text-fg">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          onUpload={() => setUploading(true)}
          onDownload={() => download()}
          onSignOut={() => void signOut()}
        />
        <main className="min-h-0 flex-1">
          {/* A view that throws should cost the reader that view, not the
              sidebar, the palette and their place in the product. The key
              resets the boundary whenever they navigate. */}
          <ErrorBoundary what="This view" resetKey={`${projectId ?? ''}|${view}|${openPath ?? ''}`}>
          {!projectId ? (
            <EmptyState title="Choose a project" hint="Pick one from the left, or press ⌘K to search everything." />
          ) : view === 'files' ? (
            <FilesView
              projectId={projectId}
              onRename={(path, name) => setRenaming({ path, name })}
              onDelete={paths => setDeleting(paths)}
              onDownload={paths => download(paths)}
              onUpload={() => setUploading(true)}
            />
          ) : view === 'activity' ? (
            <ActivityView projectId={projectId} />
          ) : view === 'locks' ? (
            <LocksView projectId={projectId} />
          ) : view === 'sync' ? (
            <SyncView projectId={projectId} />
          ) : (
            <CommentsView projectId={projectId} />
          )}
          </ErrorBoundary>
        </main>
      </div>

      <CommandPalette
        onUpload={() => setUploading(true)}
        onDownload={() => download()}
        onRename={() => startRename()}
        onDelete={() => selection.size && setDeleting([...selection])}
      />

      {renaming && projectId && (
        <RenameDialog
          projectId={projectId}
          path={renaming.path}
          currentName={renaming.name}
          onClose={() => setRenaming(undefined)}
        />
      )}
      {deleting && projectId && (
        <DeleteDialog projectId={projectId} paths={deleting} onClose={() => setDeleting(undefined)} />
      )}
      {uploading && projectId && (
        <UploadDialog projectId={projectId} onClose={() => setUploading(false)} />
      )}

      <TransferTray />
    </div>
  )
}
